import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useForm } from "react-hook-form";
import { useCallback, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { MotivoAfastamentoForm, defaultMotivoAfastamentoForm, MotivoAfastamentoSchema } from "../../../schemas/MotivoAfastamento_lista/motivo_afastamento";
import { MotivoAfastamento } from "../../../../domain/models/motivo_afastamento";
import { FormSwitchInput } from "../../../../../../common/components/masked_inputs/react_forms/form_switch_input";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";

/**
 * CadastroMotivoAfastamentoModal
 */

type Props = {
  open: boolean,
  saving: boolean,
  selectedMotivoAfastamento: MotivoAfastamento | null,
  onSubmit: (form: MotivoAfastamentoForm) => void,
  onOpenChangeHistory: () => void,
  onClose: () => void,
};

export const CadastroMotivoAfastamentoModal = ({
  open,
  saving,
  selectedMotivoAfastamento,
  onSubmit,
  onOpenChangeHistory,
  onClose,
}: Props) => {
  const hookForm = useForm<MotivoAfastamentoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: defaultMotivoAfastamentoForm,
    resolver: zodResolver(MotivoAfastamentoSchema),
  });

  useEffect(() => {
    if (open) {
      if (selectedMotivoAfastamento) {
        hookForm.setValue('ativo', selectedMotivoAfastamento.ativo ? 1 : 0);
        hookForm.setValue('descricaoMotivo', selectedMotivoAfastamento.descricaoMotivo ?? '');
      } else {
        hookForm.reset();
      }
    }
  }, [
    hookForm,
    open,
    selectedMotivoAfastamento,
  ]);

  const handleSubmit = useCallback((form: MotivoAfastamentoForm) => {
    if (!saving) onSubmit(form);
  }, [saving, onSubmit]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: 'form',
          onSubmit: hookForm.handleSubmit(handleSubmit),
        }
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "45rem",
          },
        },
      }}>

      <DialogTitle>
        Cadastrar Motivo Afastamento
      </DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow">
            <>
              <FormSwitchInput
                name='ativo'
                readOnly={saving}
                reactHookForm={hookForm} />

              <DefaultFormInput
                name='descricaoMotivo'
                label='Motivo Demissão'
                readOnly={saving}
                required={true}
                multiline={true}
                rows={3}
                reactHookForm={hookForm}
              />

            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex px-4 pb-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedMotivoAfastamento) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={onOpenChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>

            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green"
                  disabled={saving}>
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}